import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import { Flex } from 'rebass';
import useNotify from '../../hooks/useNotify';
import colors from '../../theme/colors';
import theme from '../../theme';
import Button from '../Button/Button';
import Form from '../Form/Form';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { SET_DEFAULT_PAYMENT_METHOD } from './stripe.queries';

type SetupIntentModalProps = {
  onClose: () => void;
  email?: string;
  phone?: string;
  message?: string;
};

export default function SetupIntentModal(props: SetupIntentModalProps) {
  const stripe = useStripe();
  const elements = useElements();

  const { showSuccess, showError } = useNotify();

  const [setStripeDefaultPaymentMethod] = useMutation<
    {
      setStripeDefaultPaymentMethod: any;
    },
    { paymentMethodId: string }
  >(SET_DEFAULT_PAYMENT_METHOD);

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href, // optional if you're not redirecting
        },
        redirect: 'if_required', // prevents full page redirect in most cases
      });

      if (result.error) {
        showError(result.error.message ?? 'Error saving payment method');
      } else {
        const paymentMethodId = result.setupIntent?.payment_method as string;
        // You can notify your backend here if needed

        await setStripeDefaultPaymentMethod({
          variables: {
            paymentMethodId,
          },
        });

        showSuccess('Payment method updated successfully');
        props.onClose?.();
      }
    } finally {
      setLoading(false);
    }
  };

  const [formComplete, setFormComplete] = useState<boolean>(false);
  const [formCollapsed, setFormCollapsed] = useState<boolean>(true);
  const [formEmpty, setFormEmpty] = useState<boolean>(false);

  return (
    <Modal title="Payment Method" onClose={props.onClose}>
      <ModalBody>
        <Form mt={3} sx={{ gap: 2 }}>
          {props.message && <WarnMessage>{props.message}</WarnMessage>}
          <PaymentElement
            options={{
              defaultValues: {
                billingDetails: {
                  email: props.email,
                  phone: props.phone,
                },
              },
            }}
            onChange={(paymentElementEvent) => {
              // console.log('onChange', paymentElementEvent);
              setFormComplete(paymentElementEvent.complete);
              setFormCollapsed(paymentElementEvent.collapsed);
              setFormEmpty(paymentElementEvent.empty);
            }}
          />
        </Form>
      </ModalBody>
      <ModalFooter justifyContent="space-between">
        <Button variant="outline" icon="slash-circle" onClick={props.onClose}>
          Cancel
        </Button>

        <Button
          icon="save"
          onClick={handleSubmit}
          disabled={loading || !stripe || !elements || !formComplete}
        >
          Save Payment Method
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const WarnMessage = styled(Flex)`
  background-color: ${colors.statusOrange};
  color: white;
  padding: 8px 16px;
  border-radius: ${theme.radii.default}px;
  align-items: center;
  justify-content: center;
`;
